import { cn } from '@/lib/utils';
import type {
  SectionCompletion,
  SectionProgressEntry,
  TemplateSection,
} from '@/types/section-guidance';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, Circle, CircleDot, Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionHintBubble } from './section-hint-bubble';

interface SectionAccordionProps {
  sections: TemplateSection[];
  activeSectionIndex: number;
  entries: SectionProgressEntry[];
  hint: string | null;
  isAnalyzing: boolean;
  onSectionClick: (index: number) => void;
  className?: string;
}

const CompletionBadge = ({ completion }: { completion: SectionCompletion }) => {
  switch (completion) {
    case 'complete':
      return <CheckCircle2 className="size-5 text-green-600" />;
    case 'partial':
      return <CircleDot className="size-5 text-yellow-600" />;
    case 'incomplete':
    default:
      return <Circle className="size-5 text-gray-400" />;
  }
};

export const SectionAccordion = ({
  sections,
  activeSectionIndex,
  entries,
  hint,
  isAnalyzing,
  onSectionClick,
  className,
}: SectionAccordionProps) => {
  const { t } = useTranslation();

  // Calculate overall progress
  const completedCount = entries.filter(
    (e) => e.completion === 'complete',
  ).length;
  const totalCount = sections.length;
  const progressPercentage =
    totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const sectionRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Auto-scroll to active section when it changes
  useEffect(() => {
    const el = sectionRefs.current.get(activeSectionIndex);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeSectionIndex]);

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <p className="text-xs font-light text-gray-500 uppercase">
            {t('templates:sections')}
          </p>
          {totalCount > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-gray-700">
                {completedCount}/{totalCount}
              </span>
              <div className="h-1.5 w-16 rounded-full bg-gray-200">
                <div
                  className="bg-primary h-full rounded-full transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          )}
        </div>
        {isAnalyzing && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Loader2 className="size-3 animate-spin" />
            <span>{t('home:sectionGuidance.analyzing')}</span>
          </div>
        )}
      </div>

      {hint && <SectionHintBubble hint={hint} />}

      <div className="flex flex-col gap-2">
        {sections.map((section, index) => {
          const isActive = index === activeSectionIndex;
          const entry = entries.find((e) => e.sectionIndex === index);
          const completion = entry?.completion ?? 'incomplete';

          return (
            <div
              key={index}
              ref={(el) => {
                if (el) sectionRefs.current.set(index, el);
                else sectionRefs.current.delete(index);
              }}>
              <button
                onClick={() => onSectionClick(index)}
                className={cn(
                  'w-full rounded-lg border px-4 py-2.5 text-left transition-all',
                  isActive
                    ? 'border-primary bg-primary/10'
                    : 'border-gray-300 bg-gray-100 hover:bg-gray-200',
                )}>
                <div className="flex items-center justify-between gap-3">
                  <p className="flex-1 text-sm font-medium">{section.title}</p>
                  <CompletionBadge completion={completion} />
                </div>
              </button>

              <AnimatePresence>
                {isActive && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    className="overflow-hidden">
                    <div className="px-4 pt-2 pb-1">
                      <p className="text-sm text-gray-600">{section.prompt}</p>

                      {entry?.evidence && (
                        <p className="mt-2 text-xs text-gray-500 italic">
                          {entry.evidence}
                        </p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};
